import type { ApproachingPromotion } from '#types/cart'
import type { Order } from '#types/components/checkout/order'

const baseEvent = {
  eventCategory: 'Enhanced Ecommerce',
  eventLabel: undefined,
  nonInteractive: true,
  customMetrics: {},
  customVariables: {},
  _clear: true
}

const baseLoadEvent = {
  event: 'loadEventData',
  eventCategory: 'Payment',
  eventAction: 'Method Selection',
  eventLabel: undefined,
  nonInteractive: 0,
  customMetrics: {},
  customVariables: {}
}

type Step = 'cart' | 'shipping' | 'billing' | 'summary'

type CheckoutEventActions
  = 'Checkout - Payment'
    | 'Checkout - Cart'
    | 'Checkout Option'
    | 'Order Confirmation'
    | 'Checkout - Shipping'
    | 'Checkout - Summary'

const eventActions: Record<Step, CheckoutEventActions> = {
  cart: 'Checkout - Cart',
  shipping: 'Checkout - Shipping',
  billing: 'Checkout - Payment',
  summary: 'Checkout - Summary'
}

const CheckoutStepNumber = {
  cartEmpty: 0,
  cart: 1,
  shipping: 2,
  billing: 3,
  summary: 4
}

const getOrderPromoActionFields = (cart) => {
  let totalPrice = 0
  const promoCodes: string[] = []
  const promoTypes: string[] = []
  const promoCampaigns: string[] = []

  cart?.orderPromotions?.forEach((promo) => {
    totalPrice += Math.abs(promo.price)
    promoCodes.push(promo.couponCode || promo.itemText)
    promoTypes.push(promo.couponCode ? 'CODE' : 'AUTO')
    if (promo.campaignId) promoCampaigns.push(promo.campaignId)
  })

  return {
    orderPromoAmount: totalPrice,
    orderPromoCode: promoCodes?.join('|'),
    orderPromoType: promoTypes?.join('|'),
    orderPromoCampaignId: promoCampaigns?.join('|'),
  }
}

const getShippingActionFields = (shipment, { shipmentCodes = [] }: { shipmentCodes?: string[] } = {}) => {
  let shippingPromoCode = shipment.shippingPromotions?.find(({ couponCode }) => !!couponCode)?.couponCode
  let shippingPromoType: string | undefined
  if (shipment.shippingPromotions?.length)
    shippingPromoType = shippingPromoCode ? 'CODE' : 'AUTO'

  if (!shippingPromoCode)
    shippingPromoCode = shipment.shippingPromotions?.find((promo) => !!promo.promotionId)?.promotionId
  return {
    shippingMethod: [...new Set(shipmentCodes)].join('/').toUpperCase(),
    shippingPromoCode,
    shippingPromoType,
    shippingPromoAmount: (shipment.shippingPromotions || [])
      .reduce((amount, promo) => promo.appliedDiscount?.amount ? amount + Math.abs(promo.price) : amount, 0),
    shippingPromoCampaignId: shipment.shippingPromotions?.find(({ campaignId }) => !!campaignId)?.campaignId,
  }
}

const getActionField = (cart, promotions: ApproachingPromotion[], step, option) => ({
  step: cart.items.length ? CheckoutStepNumber[step] : CheckoutStepNumber.cartEmpty,
  option: option || ((['cart', 'shipping'].includes(step?.toLowerCase())
    ? `Shipping Option: ${cart.shippingMethods.map(({ label }) => label).join('/')}`
    : undefined)),
  shippingThreshold: promotions.find(
    (promo: ApproachingPromotion) => promo.promotionClass === 'SHIPPING'
  )?.thresholdStatus,
  ...getOrderPromoActionFields(cart),
  ...(cart.shippingMethods?.[0]
    ? getShippingActionFields(cart.shippingMethods?.[0], {
        shipmentCodes: cart.shippingMethods?.map(({ code }) => code).filter(Boolean)
      })
    : {})
})

const getGiftCardActionFields = (order: Order) => {
  const giftCardPayments = order.paymentMethod.filter((type) => type.code === 'GIFT_CARD')

  return {
    giftCard: giftCardPayments.length > 0,
    giftCardAmount: giftCardPayments.reduce((sum, payment) => sum + (payment.additionalData?.amount || 0), 0)
  }
}

const getOrderActionFields = (order: Order, isExpress?: boolean) => ({
  affiliation: useSiteId(),
  coom_01: order?.billingAddress?.email,
  id: order?.orderNumber,
  paymentType: `${order?.paymentMethod.map((type) => type?.code).join(', ')}: ${isExpress ? 'Express' : 'Standard'} Checkout`,
  revenue: order?.totals?.totalWithoutTaxAfterDiscount,
  rewardsAmount: order?.paymentMethod.find(({ code }) => code === 'LOYALTY_POINTS')?.additionalData?.amount || 0,
  shipping: order?.totals?.shipping,
  shippingThreshold: localStorage.getItem('shippingThreshold') || undefined,
  tax: order?.totals?.merchandiseTotalTax,
  total: order?.totals?.total
})

export default {
  onCheckout: (cart, promotions: ApproachingPromotion[], step: Step, option?) => ({
    ...baseEvent,
    event: 'checkout',
    eventAction: eventActions[step],
    ecommerce: {
      currencyCode: cart.currency,
      checkout: {
        actionField: getActionField(cart, promotions, step, option),
        products: getProductObjectList(cart.items, {
          isCartItem: true,
          omitPosition: true
        })
      }
    }
  }),
  onCheckoutCompletion: (order: Order, isExpress?: boolean) => ({
    ...baseEvent,
    event: 'checkoutCompletion',
    eventAction: 'Order Confirmation',
    ecommerce: {
      currencyCode: order.totals.currency,
      purchase: {
        actionField: {
          ...getOrderActionFields(order, isExpress),
          ...getGiftCardActionFields(order),
          ...getOrderPromoActionFields(order),
          ...(order.shipments?.[0]
            ? getShippingActionFields(order.shipments?.[0], {
                shipmentCodes: order.shipments?.map(
                  ({ code, shippingMethod }) => shippingMethod?.code || code
                ).filter(Boolean)
              })
            : {})
        },
        products: getProductObjectList(order.items, {
          isCartItem: true,
          omitPosition: true
        })
      }
    }
  }),
  onCheckoutOption: (cart, promotions: ApproachingPromotion[], step: Step, method) => {
    const option = `${step === 'billing' ? 'Payment' : 'Shipping'} Method: ${method}`

    return {
      ...baseEvent,
      event: 'checkoutOption',
      eventAction: 'Checkout Option',
      eventLabel: option,
      ecommerce: {
        currencyCode: cart.currency,
        checkout_option: {
          actionField: {
            ...getActionField(cart, promotions, step, option),
            ...(step === 'billing' ? { paymentType: method } : {})
          }
        }
      }
    }
  },
  onLoadEventData: (option) => ({
    ...baseLoadEvent,
    eventLabel: option
  }),
  onClickDeliveryMethod: (option, customVariables: { shippingStoreId?: string } = {}) => ({
    ...baseLoadEvent,
    eventCategory: 'Delivery',
    eventLabel: option,
    customVariables
  }),
  onAddOrderImpression: () => ({
    ...baseLoadEvent,
    eventCategory: 'Order Confirmation',
    eventAction: 'Add Order',
    eventLabel: 'Impression'
  }),
  onAddOrderClick: () => ({
    ...baseLoadEvent,
    eventCategory: 'Order Confirmation',
    eventAction: 'Add Order',
    eventLabel: 'Click',
    _clear: true
  }),
  onAddOrderSubmit: () => ({
    ...baseLoadEvent,
    eventCategory: 'Order Confirmation',
    eventAction: 'Add Order',
    eventLabel: 'Submit',
    _clear: true
  }),
  onAddOrderConfirmation: () => ({
    ...baseLoadEvent,
    eventCategory: 'Order Confirmation',
    eventAction: 'Add Order',
    eventLabel: 'Confirmation',
    _clear: true
  }),
}
